import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext(null);

const STORAGE_KEY = "vx_theme"; // "light" | "dark" | "system"

function readSystemTheme() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyThemeClass(resolved) {
  const root = document.documentElement;
  if (resolved === "dark") {
    root.classList.add("dark");
    root.style.colorScheme = "dark";
  } else {
    root.classList.remove("dark");
    root.style.colorScheme = "light";
  }
}

export function ThemeProvider({ children }) {
  // Stored user preference: 'light' | 'dark' | 'system' (default: 'system')
  const [preference, setPreference] = useState(() => {
    if (typeof window === "undefined") return "system";
    return localStorage.getItem(STORAGE_KEY) || "system";
  });

  // Current system-level preference
  const [systemTheme, setSystemTheme] = useState(readSystemTheme);

  const resolved = preference === "system" ? systemTheme : preference;

  // Sync <html class> whenever the resolved theme changes
  useEffect(() => {
    applyThemeClass(resolved);
  }, [resolved]);

  // Listen for OS-level changes to prefers-color-scheme
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setSystemTheme(e.matches ? "dark" : "light");
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  const setTheme = useCallback((val) => {
    setPreference(val);
    if (val === "system") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, val);
  }, []);

  // Convenience: cycle light → dark → system → light …
  const cycle = useCallback(() => {
    setTheme(preference === "light" ? "dark" : preference === "dark" ? "system" : "light");
  }, [preference, setTheme]);

  return (
    <ThemeContext.Provider value={{ preference, resolved, systemTheme, setTheme, cycle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
